/**
 * WatchReelButton — small pill that plays the creator's reel / video
 * inline (in a lightbox modal) when the URL is embeddable (YouTube /
 * Instagram / TikTok / direct MP4). Falls back to opening the URL in a
 * new tab for anything the parseEmbed helper can't handle (X, private
 * pages, generic links).
 *
 * v133 (Feb 2026) — takes a `url` prop and infers the platform from
 * the URL so the icon + label match (Instagram / YouTube / TikTok /
 * X / generic video).
 * v146 (Feb 2026) — tap now opens an in-app lightbox with the actual
 * video embedded when possible; the old "always external link"
 * behaviour was inconsistent with the standalone Video block, which
 * confused viewers ("why does this reel open in-app but that one
 * throws me to Instagram?").
 */
import { useState } from "react";
import { PlayCircle, Instagram, Youtube, Twitter, Music2 } from "lucide-react";
import { parseEmbed } from "./InlineVideoEmbed";
import MediaLightbox from "./MediaLightbox";
import { externalLinkClickHandler } from "../lib/openExternal";

function detectPlatform(url) {
  const u = (url || "").toLowerCase();
  if (!u) return { name: "generic", label: "Watch reel", Icon: PlayCircle };
  if (u.includes("instagram.com")) return { name: "instagram", label: "Watch reel", Icon: Instagram };
  if (u.includes("youtube.com") || u.includes("youtu.be")) return { name: "youtube", label: "Watch video", Icon: Youtube };
  if (u.includes("tiktok.com")) return { name: "tiktok", label: "Watch on TikTok", Icon: Music2 };
  if (u.includes("twitter.com") || u.includes("x.com")) return { name: "x", label: "Watch on X", Icon: Twitter };
  return { name: "generic", label: "Watch video", Icon: PlayCircle };
}

export function WatchReelButton({ url, size = "sm", className = "", testid = "watch-reel-btn" }) {
  const [open, setOpen] = useState(false);
  if (!url) return null;
  const { label, Icon, name } = detectPlatform(url);
  const sizing = size === "lg"
    ? "px-3 py-2 text-sm"
    : "px-2 py-1 text-[11px]";

  // v146 — Try to embed the video inline. `parseEmbed` returns a
  // descriptor for YouTube / Instagram (reel/p/tv) / TikTok / direct
  // video files. Anything else falls back to an external tab so we
  // never trap the viewer on an un-playable modal.
  const embed = parseEmbed(url);
  const canEmbedInline = !!embed && (embed.kind === "iframe" || embed.kind === "video");

  const commonProps = {
    className: `inline-flex items-center gap-1.5 border-2 border-ink bg-sun font-bold shadow-brutalSm hover:-translate-y-0.5 transition-transform ${sizing} ${className}`,
    onClick: (e) => {
      // Stop propagation so the pill inside a product card doesn't
      // also trigger the card's own onClick.
      e.stopPropagation();
      if (canEmbedInline) {
        e.preventDefault();
        setOpen(true);
      }
    },
    "data-testid": testid,
    "data-platform": name,
    title: canEmbedInline
      ? `Play the creator's video inline (${name})`
      : `Watch the creator's video (${name})`,
  };

  return (
    <>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        {...commonProps}
        onClick={externalLinkClickHandler(url, commonProps.onClick)}
      >
        <Icon className={size === "lg" ? "w-4 h-4" : "w-3 h-3"} strokeWidth={2.5} />
        {label}
      </a>
      {open && canEmbedInline && (
        <MediaLightbox
          items={[
            embed.kind === "iframe"
              ? { kind: "iframe", src: embed.src, aspect: embed.aspect }
              : { kind: "video", src: embed.src },
          ]}
          startIndex={0}
          onClose={() => setOpen(false)}
          testid={`${testid}-lightbox`}
        />
      )}
    </>
  );
}

export default WatchReelButton;
